import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleFooterLayout } from '@user/layouts/SimpleFooterLayout';
import { useNavigate, useLocation } from 'react-router';
import { ConfirmForm } from './ConfirmForm';
import { FlatButton } from '@framework/ui/atoms';
import BalusLogo from '@assets/balus-logo-brand-horizontal.svg';

/**
 * メールアドレス変更(EPIC-542)の確認メール内リンクの遷移先ページ。
 * requestMailAddressChange が発行した確認トークンをクエリパラメータ(token)から受け取る。
 * 未ログインでもアクセス可能(確認リンクを別デバイス/別ブラウザで開く場合があるため)。
 */
export const MailAddressChangePage: React.FC<unknown> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location]);
  const success = useMemo(() => new URLSearchParams(location.search).get('success'), [location]);

  const handleConfirmSuccess = useCallback(() => {
    // コンポーネントの state をリセットしても結果画面を維持するため、URLパラメータで状態を保持する
    // (PasswordResetPage と同じ方式)。
    const params = new URLSearchParams();
    params.set('success', 'true');
    navigate({ search: params.toString() }, { replace: true });
  }, [navigate]);

  const handleMoveHome = useCallback(() => {
    window.location.href = '/';
  }, []);

  return (
    <SimpleFooterLayout>
      <div className="mx-auto flex flex-col items-center px-4 pb-8 pt-16 md:container">
        <img src={BalusLogo} className="w-64" alt="" />

        <div className="py-8 text-3xl font-bold">{t('auth.mailAddressConfirm.pageTitle')}</div>

        <div className="rounded-lg border border-gray-500 p-8" style={{ width: '600px' }}>
          {success ? (
            <>
              <div className="text-2xl font-bold">{t('auth.mailAddressConfirm.successTitle')}</div>
              <div className="pt-8">{t('auth.mailAddressConfirm.successDescription')}</div>

              <div className="pb-8 pt-16 text-center">
                <FlatButton className="px-16" color="brand" onClick={handleMoveHome}>
                  {t('auth.accountConnected.goHome')}
                </FlatButton>
              </div>
            </>
          ) : token ? (
            <ConfirmForm token={token} onConfirmSuccess={handleConfirmSuccess} />
          ) : (
            <div className="py-4">{t('auth.mailAddressConfirm.invalidLink')}</div>
          )}
        </div>
      </div>
    </SimpleFooterLayout>
  );
};
